import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useListItems, useEventGroups } from 'modules/common/hooks';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import CmsSportCoupon from 'modules/cms-sport-coupon/index.js';
import PrebuiltBetBuilderCoupon from 'modules/pre-built-bet-builder-coupon/index.js';
import PriceBoostCoupon from 'modules/price-boost-coupon/index.js';
import CmsCouponsListHeader from './cms-coupons-list-header/index.js';
import { resolveCouponGroupInfos } from './utils.js';

const { FlexPane } = MojitoPresentation.Components;
const { selectEventGroupNames, selectState: selectEventGroupsState } =
    MojitoServices.EventGroups.selectors;

const CmsCouponsList = props => {
    const {
        mojitoTools: { config, instanceId, style },
        couponsListId,
    } = props;

    const groupIds = useListItems(couponsListId);
    const eventGroups = useEventGroups(groupIds, instanceId);
    const eventGroupNames = useSelector(state => selectEventGroupNames(state));
    const eventGroupMarketOptions = useSelector(
        state => selectEventGroupsState(state).marketOptions
    );
    const { priceBoost: priceBoostMarketType, preBuiltBetBuilder: preBuiltBetBuilderMarketType } =
        config.specificMarketTypes;
    const cmsCoupons = useMemo(() => {
        return resolveCouponGroupInfos(groupIds, eventGroups, eventGroupNames)
            .map(couponGroupInfo => {
                const marketOptions = eventGroupMarketOptions[couponGroupInfo.eventGroupId] || [];
                const isPriceBoostedCoupon = marketOptions.some(
                    ({ marketLineType }) => marketLineType === priceBoostMarketType
                );
                const isPreBuiltBetBuilderCoupon = marketOptions.some(
                    ({ marketLineType }) => marketLineType === preBuiltBetBuilderMarketType
                );
                return {
                    eventGroupId: couponGroupInfo.eventGroupId,
                    sectionId: couponGroupInfo.id,
                    couponName: couponGroupInfo.name,
                    sportId: couponGroupInfo.sportId,
                    eventGroup: couponGroupInfo.events,
                    isPriceBoostedCoupon,
                    isPreBuiltBetBuilderCoupon,
                };
            })
            .filter(coupon => !!coupon.couponName && coupon.eventGroup.length > 0);
    }, [
        groupIds,
        eventGroups,
        eventGroupNames,
        eventGroupMarketOptions,
        priceBoostMarketType,
        preBuiltBetBuilderMarketType,
    ]);

    if (cmsCoupons.length === 0) {
        return null;
    }

    return cmsCoupons.map(coupon => {
        const { isPriceBoostedCoupon, isPreBuiltBetBuilderCoupon } = coupon;
        const shouldShowCmsSportCoupon = !isPriceBoostedCoupon && !isPreBuiltBetBuilderCoupon;

        return (
            <FlexPane
                key={coupon.eventGroupId}
                id={coupon.sectionId}
                class="ta-CmsSportCouponSection"
                config={style.container}
            >
                <CmsCouponsListHeader
                    config={config.cmsCouponsListHeader}
                    couponName={coupon.couponName}
                    sportId={coupon.sportId}
                    isPriceBoostedCoupon={isPriceBoostedCoupon}
                    isPreBuiltBetBuilderCoupon={isPreBuiltBetBuilderCoupon}
                />
                {shouldShowCmsSportCoupon && (
                    <CmsSportCoupon
                        config={config.cmsSportCoupon}
                        couponId={coupon.eventGroupId}
                        couponName={coupon.couponName}
                        sportId={coupon.sportId}
                    />
                )}
                {isPriceBoostedCoupon && (
                    <PriceBoostCoupon
                        config={config.priceBoostCoupon}
                        boostedGroupId={coupon.eventGroupId}
                        sportId={coupon.sportId}
                    />
                )}
                {isPreBuiltBetBuilderCoupon && (
                    <PrebuiltBetBuilderCoupon
                        config={config.prebuiltBetBuilderCoupon}
                        pbbbGroupId={coupon.eventGroupId}
                    />
                )}
            </FlexPane>
        );
    });
};

CmsCouponsList.getStyle = function (config, mode, merge) {
    return {
        container: merge(config?.container, {
            style: { scrollMargin: `${config.scrollOffset} 0 0 0` },
        }),
    };
};

export default CmsCouponsList;
